import React from "react";
import {
  VStack,
  HStack,
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Checkbox,
  Spinner,
} from "@chakra-ui/react";
import { UserProto } from "build-ts/interface";
import { AppIcons } from "constants/icons";
import Table, { TableColumn } from "components/common/Table";
import ConfirmationModal from "common/modals/ConfirmationModal";
import { epochConverterWithTimeZone } from "utils/dateUtils";

const DATE_FORMAT = "dd-MMM-yyyy, hh:mm a";

interface UserManagementPanelProps {
  defaultTimeZone: string;
  users: UserProto[];
  loading: boolean;
  selectedUsers: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  onRowClick: (user: UserProto) => void;
  onNewUserClick: () => void;
  onDeleteSelected: () => void;
  // Create modal
  isCreateOpen: boolean;
  onCreateClose: () => void;
  formName: string;
  onFormNameChange: (value: string) => void;
  formEmail: string;
  onFormEmailChange: (value: string) => void;
  formPassword: string;
  onFormPasswordChange: (value: string) => void;
  formIsAdmin: boolean;
  onFormIsAdminChange: (value: boolean) => void;
  showPassword: boolean;
  onToggleShowPassword: () => void;
  isSubmitting: boolean;
  onCreateSubmit: () => void;
  // Edit modal
  isEditOpen: boolean;
  onEditClose: () => void;
  editingUser: UserProto | null;
  editName: string;
  onEditNameChange: (value: string) => void;
  editIsAdmin: boolean;
  onEditIsAdminChange: (value: boolean) => void;
  isSaving: boolean;
  onSaveEdit: () => void;
  // Delete confirmation
  showDeleteConfirmation: boolean;
  onCloseDeleteConfirmation: () => void;
  onConfirmDelete: () => void;
  isDeleting: boolean;
  selectedUserName: string;
}

const UserManagementPanel: React.FC<UserManagementPanelProps> = ({
  defaultTimeZone,
  users,
  loading,
  selectedUsers,
  onSelectionChange,
  onRowClick,
  onNewUserClick,
  onDeleteSelected,
  isCreateOpen,
  onCreateClose,
  formName,
  onFormNameChange,
  formEmail,
  onFormEmailChange,
  formPassword,
  onFormPasswordChange,
  formIsAdmin,
  onFormIsAdminChange,
  showPassword,
  onToggleShowPassword,
  isSubmitting,
  onCreateSubmit,
  isEditOpen,
  onEditClose,
  editingUser,
  editName,
  onEditNameChange,
  editIsAdmin,
  onEditIsAdminChange,
  isSaving,
  onSaveEdit,
  showDeleteConfirmation,
  onCloseDeleteConfirmation,
  onConfirmDelete,
  isDeleting,
  selectedUserName,
}) => {
  const columns: TableColumn<UserProto>[] = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: user => (
        <Tooltip label={user.header?.name} placement="top">
          <Text fontWeight="500" noOfLines={2} wordBreak="break-word" whiteSpace="normal" mb={0}>
            {user.header?.name ?? "-"}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: user => <Text>{user.email ?? "-"}</Text>,
    },
    {
      key: "is_admin",
      label: "Admin",
      width: "100px",
      render: user =>
        user.is_admin ? <Badge colorScheme="purple">Admin</Badge> : <Badge colorScheme="gray">User</Badge>,
    },
    {
      key: "created",
      label: "Created At",
      sortable: true,
      render: user =>
        user.header?.created_date_ms
          ? epochConverterWithTimeZone(user.header.created_date_ms, DATE_FORMAT, defaultTimeZone)
          : "-",
    },
    {
      key: "last_modified",
      label: "Last Modified",
      sortable: true,
      render: user =>
        user.header?.last_modified_date_ms
          ? epochConverterWithTimeZone(user.header.last_modified_date_ms, DATE_FORMAT, defaultTimeZone)
          : "-",
    },
  ];

  const headerActions =
    selectedUsers.size > 0 ? (
      <HStack spacing={4}>
        {selectedUsers.size === 1 && (
          <Button
            onClick={onDeleteSelected}
            colorScheme="red"
            bg="red.600"
            color="white"
            _hover={{ bg: "red.700" }}
            size="sm"
            leftIcon={<Icon as={AppIcons.trashLine} />}
          >
            Delete
          </Button>
        )}
        <Button
          onClick={() => onSelectionChange(new Set())}
          variant="outline"
          size="sm"
          leftIcon={<Icon as={AppIcons.closeLine} />}
        >
          Cancel
        </Button>
      </HStack>
    ) : null;

  return (
    <>
      <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
        <HStack justify="space-between" w="full" px={3} flexShrink={0}>
          <HStack spacing={4}>
            <Icon as={AppIcons.users} boxSize={7} color="fg.default" />
            <Text fontSize="lg" mb={0} fontWeight="700">
              Manage Users
            </Text>
          </HStack>
          <Button
            onClick={onNewUserClick}
            colorScheme="purple"
            size="md"
            bg="purple.700"
            color="white"
            _hover={{ bg: "purple.600" }}
            leftIcon={<Icon as={AppIcons.add} boxSize={3} color="white" />}
          >
            New User
          </Button>
        </HStack>
        <Box flex="1" overflow="hidden" width="100%">
          <Table
            data={users}
            columns={columns}
            loading={loading}
            emptyMessage="No users found."
            pagination={{ total: users.length, offset: 0, has_more: false }}
            selectable={true}
            selectedItems={selectedUsers}
            onSelectionChange={onSelectionChange}
            getItemId={user => user.header?.id ?? ""}
            onRowClick={onRowClick}
            headerActions={headerActions}
          />
        </Box>
      </VStack>

      {/* Create User Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input placeholder="Full name" value={formName} onChange={e => onFormNameChange(e.target.value)} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={formEmail}
                  onChange={e => onFormEmailChange(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={formPassword}
                    onChange={e => onFormPasswordChange(e.target.value)}
                  />
                  <InputRightElement>
                    <Button variant="ghost" size="sm" onClick={onToggleShowPassword} tabIndex={-1}>
                      <Icon as={showPassword ? AppIcons.viewOff : AppIcons.viewOn} />
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={formIsAdmin}
                  onChange={e => onFormIsAdminChange(e.target.checked)}
                  colorScheme="purple"
                >
                  Admin user
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              onClick={onCreateSubmit}
              isLoading={isSubmitting}
              isDisabled={!formName.trim() || !formEmail.trim() || !formPassword.trim()}
            >
              {isSubmitting ? <Spinner size="sm" /> : "Create User"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input placeholder="Full name" value={editName} onChange={e => onEditNameChange(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input value={editingUser?.email ?? ""} isReadOnly variant="filled" color="gray.500" />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={editIsAdmin}
                  onChange={e => onEditIsAdminChange(e.target.checked)}
                  colorScheme="purple"
                >
                  Admin user
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              onClick={onSaveEdit}
              isLoading={isSaving}
              isDisabled={!editName.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={onCloseDeleteConfirmation}
        onConfirm={onConfirmDelete}
        title="Delete User"
        text={`Are you sure you want to delete "${selectedUserName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />
    </>
  );
};

export default UserManagementPanel;
